import { useMemo, useState } from 'react';

import type {
  AlineSelection,
  AlineSelectionIntersection,
  NodeBoundingBox,
  RemoteSelection,
  RemoteSelectionIntersection,
  RenderModelPage,
  RenderOptions,
  SpellcheckRangeRef,
} from '@aline/common';
import {
  CachedRenderer,
  flatMapObjectValues,
  isParagraphElement,
  typedGroupBy,
} from '@aline/common';
import { sortBy } from 'ramda';
import { Editor, Point, Range } from 'slate';
import type { NodeEntry } from 'slate';
import { useEditorFindStateSelector } from 'src/editor/editor-find-state-registry';
import { useEditorTabStateSelector } from 'src/editor/editor-tab-state-registry';
import { Queries } from 'src/editor/queries';
import { useSpellcheckStateSelector } from 'src/editor/spellcheck-state-registry';
import { useEditorViewPreferences } from 'src/editor/vars';
import { useCurrentUserId } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import { useDocumentParams } from 'src/helpers';
import {
  useActiveCommentId,
  useDebouncedValue,
  useGetCurrentDoc,
  useGetDocumentComments,
  useGetEditorUserState,
} from 'src/hooks';
import { useEditingDisabled } from 'src/store/document/selectors';
import { themeV5 } from 'src/theme';

import { useEditorOnChangeCounter, useFocused } from '.';
import { useActiveSearchHit } from '../components/FindPopper';

export type CanvasDrawInstruction = ReturnType<
  typeof usePageDrawInstructions
>['canvasDrawInstructions'][number];

export type ReactDrawInstruction = ReturnType<
  typeof usePageDrawInstructions
>['reactDrawInstructions'][number];

export interface UsePageDrawInstructionsOptions {
  pageHeightPx: number;
  pageWidthPx: number;
  rMRemoteSelections: Record<string, RemoteSelection[]>;
  rMSelections: AlineSelection[] | null;
}

export function usePageDrawInstructions(
  editor: Editor,
  rMPageEntry: NodeEntry<RenderModelPage>,
  options: UsePageDrawInstructionsOptions
) {
  const { pageHeightPx, pageWidthPx } = options;
  const renderOptions = useRenderPageOptions(editor, rMPageEntry, options);

  /**
   * Re-render on every editor change. This is necessary for the render context to update selectors
   * on the editor state.
   */
  useEditorOnChangeCounter();

  const pageBoundingBox: NodeBoundingBox<RenderModelPage> = useMemo(
    () => ({
      heightPx: pageHeightPx,
      leftPx: 0,
      rMNodeEntry: rMPageEntry,
      topPx: 0,
      type: 'node',
      widthPx: pageWidthPx,
    }),
    [pageHeightPx, pageWidthPx, rMPageEntry]
  );

  const [renderer] = useState(() => new CachedRenderer(editor));
  const drawInstructions = renderer.render(pageBoundingBox, renderOptions);

  const {
    groups: { annotationMarkers, reactDrawInstructions },
    rest: unsortedCanvasDrawInstructions,
  } = typedGroupBy(drawInstructions, {
    annotationMarkers: drawInstruction => drawInstruction.type === 'annotationMarker',
    noop: drawInstruction =>
      drawInstruction.type === 'pdfLink' || drawInstruction.type === 'pdfBookmark',
    reactDrawInstructions: drawInstruction =>
      drawInstruction.type === 'reactComponent' ||
      drawInstruction.type === 'caret' ||
      drawInstruction.type === 'error',
  });

  const canvasDrawInstructions = sortBy(
    drawInstruction => ('zIndex' in drawInstruction ? (drawInstruction.zIndex ?? 0) : 0),
    unsortedCanvasDrawInstructions
  );

  return { annotationMarkers, canvasDrawInstructions, reactDrawInstructions };
}

function useRenderPageOptions(
  editor: Editor,
  rMPageEntry: NodeEntry<RenderModelPage>,
  options: UsePageDrawInstructionsOptions
): Omit<RenderOptions, 'rMPath'> {
  const [rMPage] = rMPageEntry;
  const { rMRemoteSelections, rMSelections } = options;

  const { documentId } = useDocumentParams();
  const { commentsById } = useGetDocumentComments(documentId);
  const { document: alineDocument } = useGetCurrentDoc();
  const { isInSigningCeremony } = useGetEditorUserState();
  const mousePageCoords = useEditorTabStateSelector(state => state.dragDrop.mousePageCoords);
  const { data: dbFieldsData } = Apollo.useGetDbFieldsQuery({ variables: { documentId } });

  const checkboxMaxRequiredByFieldId = useMemo(() => {
    const map = new Map<string, number | null>();
    for (const variable of dbFieldsData?.document.variables ?? []) {
      if (variable.variant === Apollo.MetadataVariant.Checkbox) {
        map.set(variable.nodeId, variable.maxRequired ?? null);
      }
    }
    return map;
  }, [dbFieldsData?.document.variables]);

  const canExpandAndCollapseDynamicBlocks =
    !isInSigningCeremony && alineDocument?.workflow?.state !== Apollo.WorkflowState.Complete;

  const rMSelIntersection: AlineSelectionIntersection | null = useMemo(() => {
    const rMSelection =
      rMSelections &&
      Queries.getRMLocationForActiveHeaderOrFooter(editor, rMSelections, rMPage.pageNum);
    if (!rMSelection) return null;

    return {
      ...rMSelection,
      isExpanded: Range.isExpanded(rMSelection),
      isOriginalFocus: true,
    };
  }, [editor, rMPage.pageNum, rMSelections]);

  const rMRemoteSelIntersections = useMemo(
    () =>
      flatMapObjectValues(rMRemoteSelections, candidates => {
        const rMRemoteSelection = Queries.getRMLocationForActiveHeaderOrFooter(
          editor,
          candidates,
          rMPage.pageNum
        );
        if (!rMRemoteSelection) return [];

        const rMRemoteSelectionIntersection: RemoteSelectionIntersection = {
          ...rMRemoteSelection,
          isExpanded: Range.isExpanded(rMRemoteSelection),
          isOriginalFocus: true,
        };
        return [rMRemoteSelectionIntersection];
      }),
    [editor, rMPage.pageNum, rMRemoteSelections]
  );

  const rMDropPoint = useMemo(() => {
    if (!mousePageCoords || !Point.isPoint(mousePageCoords)) return null;
    return Queries.getRMLocationForActiveHeaderOrFooter(
      editor,
      Queries.getRMPointsFromCMPoint(editor, mousePageCoords, mousePageCoords.affinity),
      rMPage.pageNum
    );
  }, [editor, mousePageCoords, rMPage.pageNum]);

  const matchCase = useEditorFindStateSelector(state => state.matchCase);
  const matchAccents = useEditorFindStateSelector(state => state.matchAccents);
  const undebouncedSearch = useEditorFindStateSelector(state => state.search);
  const { showNonPrintingCharacters, showPageBreaks } = useEditorViewPreferences();
  const spellcheckRangeRefs = useSpellcheckStateSelector(state => state.ranges);
  const spellcheckRetiredFromByParagraph = useSpellcheckStateSelector(
    state => state.retiredFromByParagraph
  );

  /**
   * Group the stable range-ref objects by the paragraph they sit in at publish time. Each
   * paragraph unwraps `.current` when it paints, so an edit does not need a new map.
   */
  const spellcheckRangesByParagraph = useMemo(() => {
    if (spellcheckRangeRefs.length === 0) return null;
    const map = new Map<string, SpellcheckRangeRef[]>();
    for (const rangeRef of spellcheckRangeRefs) {
      const range = rangeRef.current;
      if (range && Editor.hasPath(editor, range.anchor.path)) {
        const entry = Editor.above(editor, { at: range.anchor, match: isParagraphElement });
        if (entry) {
          const key = entry[1].join(',');
          const existing = map.get(key);
          if (existing) existing.push(rangeRef);
          else map.set(key, [rangeRef]);
        }
      }
    }
    return map;
  }, [editor, spellcheckRangeRefs]);

  return {
    activeCommentId: useActiveCommentId(),
    activeSearchHit: useActiveSearchHit(),
    canExpandAndCollapseDynamicBlocks,
    checkboxMaxRequiredByFieldId,
    commentsById,
    currentUserId: useCurrentUserId(),
    dynamicBlockColors: themeV5.appColors.dynamicColors,
    gridSelectionInfo: Queries.getGridSelectionInfo(editor),
    isFocused: useFocused(),
    isPDFExport: false,
    isReadOnly: useEditingDisabled(),
    matchAccents,
    matchCase,
    rMDropPoint,
    rMRemoteSelIntersections,
    rMSelIntersection,
    search: useDebouncedValue(undebouncedSearch, 200),
    showNonPrintingCharacters,
    showPageBreaks,
    spellcheckRangesByParagraph,
    spellcheckRetiredFromByParagraph,
  };
}
